import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { setPageSeo } from "../lib/seo";
import { BUSINESS } from "../lib/business";

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageSeo({
      title: "Privacy Policy — DevzCorner",
      description: "Read DevzCorner's Privacy Policy and how personal data is collected, used, and protected.",
      path: "/privacy",
    });
  }, []);

  return (
    <main className="relative bg-ink-0 text-white min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-32 pb-24 px-6 md:px-10 max-w-4xl mx-auto w-full">
        <h1 className="font-display text-4xl md:text-5xl mb-8">Privacy Policy</h1>
        <div className="prose prose-invert max-w-none text-zinc-400 font-light leading-relaxed space-y-6">
          <p>Last updated: October 2026</p>
          <p>
            This Privacy Policy explains how {BUSINESS.legalName} (&quot;{BUSINESS.brandName}&quot;, &quot;we&quot;, &quot;us&quot;),
            operating from {BUSINESS.addressOneLine}, collects, uses, and protects personal information when you use our website or contact us about our services.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">1. Information We Collect</h2>
          <p>We collect information in the following ways:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-zinc-300">Contact / project form:</strong> name, email address, project type, and the message you submit. We do not ask for a company name on this form.
            </li>
            <li>
              <strong className="text-zinc-300">Email / phone:</strong> if you write to {BUSINESS.email} or call {BUSINESS.phoneDisplay}, we receive whatever you share in that conversation.
            </li>
            <li>
              <strong className="text-zinc-300">Usage &amp; analytics:</strong> pages viewed, device/browser information, approximate location derived from IP, and similar technical data via analytics tools (see Section 4).
            </li>
            <li>
              <strong className="text-zinc-300">Payments:</strong> payment details are handled by third-party payment processors; we do not store full card or bank account numbers on our website.
            </li>
          </ul>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Respond to project inquiries and schedule discovery calls.</li>
            <li>Process orders, invoices, and support related to paid engagements.</li>
            <li>Understand and improve our website and services.</li>
            <li>Maintain security and prevent spam or abuse.</li>
          </ul>
          <p>
            Inquiry and lead data is accessed only by {BUSINESS.legalName}. We do not sell your personal information.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">3. Where Data Is Stored &amp; Who Processes It</h2>
          <p>We use trusted third-party processors to operate the site:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong className="text-zinc-300">Supabase:</strong> stores contact/project form submissions (leads) so we can follow up with you.
            </li>
            <li>
              <strong className="text-zinc-300">PostHog:</strong> product analytics and session recording to understand how visitors use the site (see Section 4).
            </li>
            <li>
              <strong className="text-zinc-300">Hosting / infrastructure providers:</strong> host and deliver the website.
            </li>
            <li>
              <strong className="text-zinc-300">Payment processors:</strong> payments are handled by third-party payment providers. Payment data you enter on a processor&apos;s checkout is governed by that provider&apos;s privacy policy.
            </li>
          </ul>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">4. Cookies, Analytics &amp; Session Recording</h2>
          <p>
            Our website uses PostHog (api host: us.i.posthog.com) for analytics. Depending on configuration, this may include cookies or similar technologies, page-view tracking, and session recording. You can block or clear cookies in your browser settings; the site should still be usable for browsing and contacting us.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">5. Retention</h2>
          <p>
            We keep form and inquiry data for as long as needed to respond to your request, manage a project engagement, or meet legal and accounting requirements. You may ask us to delete your personal information (see Section 7).
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">6. Information Protection</h2>
          <p>
            We use reasonable technical and organizational measures to protect personal information, including encrypted transport (HTTPS) and access limited to {BUSINESS.legalName}. No method of transmission or storage is completely secure.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">7. Your Rights</h2>
          <p>
            Under applicable Indian law (including the Digital Personal Data Protection Act, 2023, where applicable) and other laws that may apply to you, you may have the right to request access to, correction of, or deletion of personal information we hold about you. Contact us using the details below. We may need to verify your identity before acting on a request.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">8. Children</h2>
          <p>
            Our website and services are directed at businesses and professionals. We do not knowingly collect personal information from children.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The &quot;Last updated&quot; date at the top will change when we do.
          </p>

          <h2 className="text-white text-2xl font-display mt-8 mb-4">10. Contact Us</h2>
          <p>
            {BUSINESS.legalName}<br />
            {BUSINESS.addressOneLine}<br />
            Phone:{" "}
            <a href={BUSINESS.phoneHref} className="text-white underline hover:text-zinc-300 transition-colors">
              {BUSINESS.phoneDisplay}
            </a>
            <br />
            Email:{" "}
            <a href={`mailto:${BUSINESS.email}`} className="text-white underline hover:text-zinc-300 transition-colors">
              {BUSINESS.email}
            </a>
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
